import { Spinner } from '@atoms/Spinner';
import { Button } from '@atoms/Button';
import { useChatThread } from '@hooks/useChatThread';
import { useAuthStore } from '@store/authStore';
import { getErrorMessage } from '@services/api';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { ChatComposer } from './ChatComposer';

interface ChatThreadProps {
  partnerId: string;
  partnerName?: string;
  orderId?: string;
  compact?: boolean;
}

export function ChatThread({ partnerId, partnerName, orderId, compact }: ChatThreadProps) {
  const {
    messages,
    isLoading,
    isError,
    error,
    refetch,
    isPartnerTyping,
    sendMutation,
    notifyTyping,
    listRef,
  } = useChatThread({ partnerId, orderId, enabled: Boolean(partnerId) });
  const userId = useAuthStore((s) => s.user?.id);

  if (isLoading) {
    return (
      <div className={compact ? 'py-8' : 'flex h-full min-h-0 items-center justify-center'}>
        <Spinner />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={compact ? 'py-8' : 'flex h-full min-h-0 flex-col items-center justify-center gap-3 p-6 text-center'}>
        <p className="text-sm text-red-600">{getErrorMessage(error, 'Mesajlar yüklenemedi')}</p>
        <Button type="button" variant="secondary" onClick={() => void refetch()}>
          Tekrar dene
        </Button>
      </div>
    );
  }

  return (
    <div className={compact ? 'mt-3' : 'flex h-full min-h-0 flex-col overflow-hidden bg-slate-50'}>
      {!compact && (
        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
          <p className="font-semibold text-slate-900">{partnerName ?? 'Sohbet'}</p>
        </div>
      )}

      <div
        ref={listRef}
        className={
          compact
            ? 'mb-3 max-h-56 space-y-2 overflow-y-auto'
            : 'min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4'
        }
      >
        {messages.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">Henüz mesaj yok. İlk mesajı siz gönderin.</p>
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.clientMessageId ?? msg.id}
              message={msg}
              isOwn={msg.senderId === userId}
            />
          ))
        )}
        {isPartnerTyping && <TypingIndicator name={partnerName} />}
      </div>

      <ChatComposer
        loading={sendMutation.isPending}
        onTyping={notifyTyping}
        onSend={(content) => sendMutation.mutate(content)}
      />
    </div>
  );
}
